import React, { useState, useEffect } from 'react';
import { useBranch } from '../../../layouts/BranchContext';
import {
  Button,
  Box,
  Tooltip,
  TextField,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Typography,
  useTheme,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { AttachFile as AttachFileIcon, Clear as ClearIcon } from '@mui/icons-material';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddTopic = () => {
  const theme = useTheme();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [courseId, setCourseId] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [moduleId, setModuleId] = useState('');
  const [topic, setTopic] = useState('');
  const [topicContent, setTopicContent] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [fileName, setFileName] = useState('');
  const navigate = useNavigate();

   const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB in bytes
  const { selectedBranchId } = useBranch();

  const getOrganizationId = () => {
    try {
      const raw = sessionStorage.getItem('user') || localStorage.getItem('user') || '{}';
      const user = JSON.parse(raw) || {};
      return user.organization_id ?? null;
    } catch {
      return null;
    }
  };

  const buildOrgBranchQuery = (organizationId, branchId) => {
    const params = new URLSearchParams();
    if (organizationId !== null && organizationId !== undefined && organizationId !== '') {
      params.append('organization', organizationId);
    }
    if (branchId !== null && branchId !== undefined && branchId !== '') {
      params.append('branch', branchId);
    }
    const query = params.toString();
    return query ? `?${query}` : '';
  };

  useEffect(() => {
    const fetchCourses = async () => {
      setLoadingCourses(true);
      const token = localStorage.getItem('accessToken');

           try {
        const courseQuery = buildOrgBranchQuery(getOrganizationId(), selectedBranchId);
        const res = await axios.get(`${API_BASE_URL}/api/course/courses/${courseQuery}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setCourses(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch courses:", err);
        setSnackbarMessage("Failed to load courses");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      } finally {
        setLoadingCourses(false);
      }
    };

      fetchCourses();
  }, [selectedBranchId]);

  useEffect(() => {
    if (!courseId) {
      setModules([]);
      return;
    }

    const fetchModules = async () => {
      setLoadingModules(true);
      const token = localStorage.getItem('accessToken');

           try {
        const moduleQuery = buildOrgBranchQuery(getOrganizationId(), selectedBranchId);
        const res = await axios.get(`${API_BASE_URL}/api/course/modules/${moduleQuery}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const allModules = Array.isArray(res.data) ? res.data : [];

        const filtered = allModules.filter(
          (mod) => mod.course === parseInt(courseId) || mod.course_id === parseInt(courseId)
        );
        setModules(filtered);
      } catch (err) {
        console.error("Failed to fetch modules:", err);
        setSnackbarMessage("Failed to load modules for this course");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        setModules([]);
      } finally {
        setLoadingModules(false);
      }
    };

       fetchModules();
  }, [courseId, selectedBranchId]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setSnackbarMessage('File size exceeds 1MB limit. Please choose a smaller file.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        // Clear the file input
        e.target.value = '';
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!courseId || !moduleId || !topic.trim() || !topicContent.trim()) {
      setSnackbarMessage('Please fill all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');

      const formDataToSend = new FormData();
      formDataToSend.append('course', parseInt(courseId));
      formDataToSend.append('module', parseInt(moduleId));
      formDataToSend.append('topic_name', topic.trim());
      formDataToSend.append('topic_content', topicContent.trim());
      formDataToSend.append('organization', getOrganizationId() ?? '');
      formDataToSend.append('branch', selectedBranchId ?? '');

      if (selectedFile) {
        formDataToSend.append('pdf_file', selectedFile);
      }

      await axios.post(
        `${API_BASE_URL}/api/course/topics/`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      setShowSuccessModal(true);
      setSnackbarMessage('Topic added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      setCourseId('');
      setModuleId('');
      setTopic('');
      setTopicContent('');
      setSelectedFile(null);
      setFileName('');

    } catch (err) {
      console.error('Failed to add topic:', err);
      setSnackbarMessage(
        err.response?.data?.detail || 'Failed to add topic. Please try again.'
      );
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleCancel = () => {
    setCourseId('');
    setModuleId('');
    setTopic('');
    setTopicContent('');
    setSelectedFile(null);
    setFileName('');
  };

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box sx={{ p: 4, backgroundColor: theme.palette.background.paper }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Topic Details
          </Typography>
        </Box>
        <form onSubmit={handleSave}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>Course Name *</InputLabel>
            <Select
              value={courseId}
              label="Course Name *"
              onChange={(e) => {
                setCourseId(e.target.value);
                setModuleId('');
              }}
              disabled={loadingCourses}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
              required
            >
              {loadingCourses ? (
                <MenuItem disabled>Loading courses...</MenuItem>
              ) : courses.length === 0 ? (
                <MenuItem disabled>No courses available</MenuItem>
              ) : (
                courses.map((course) => (
                  <MenuItem key={course.id} value={course.id}>
                    {course.course_name || course.name || 'Unnamed Course'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>Module Name *</InputLabel>
            <Select
              value={moduleId}
              label="Module Name *"
              onChange={(e) => setModuleId(e.target.value)}
              disabled={!courseId || loadingModules}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
              }}
              required
            >
              {loadingModules ? (
                <MenuItem disabled>Loading modules...</MenuItem>
              ) : modules.length === 0 ? (
                <MenuItem disabled>No modules available for this course</MenuItem>
              ) : (
                modules.map((mod) => (
                  <MenuItem key={mod.id} value={mod.id}>
                    {mod.module_name || mod.name || mod.ModuleName || 'Unnamed Module'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Topic *"
            variant="outlined"
            fullWidth
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': { color: theme.palette.text.primary },
              '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
            }}
            required
          />

          <TextField
            label="Topic Content *"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={topicContent}
            onChange={(e) => setTopicContent(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': { color: theme.palette.text.primary },
              '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
            }}
            required
          />

          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<AttachFileIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            >
              Choose PDF
              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={handleFileChange}
              />
            </Button>
            {fileName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">{fileName}</Typography>
                <IconButton onClick={() => { setSelectedFile(null); setFileName(''); }} size="small">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 500, ml: 1, display: 'block', mb: 3 }}>
            Maximum file size: 1MB
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Tooltip
              title={
                !courseId ? "Select Course" :
                  !moduleId ? "Select Module" :
                    !topic ? "Enter Topic" :
                      !topicContent ? "Enter Topic Content" : ''
              }
              arrow
            >
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!topic || !courseId || !moduleId || !topicContent}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  SAVE
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
            >
              RESET
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => { navigate(-1) }}
            >
              BACK
            </Button>
          </Box>
        </form>

        <Modal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: theme.palette.background.paper,
              boxShadow: 24,
          
            }}
          >
            <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
              <Typography variant="h6" sx={{ color: theme.palette.primary.contrastText }}>
                Topic Added ✅
              </Typography>
            </Box>
            <Box sx={{ padding: '20px' }}>
              <Typography sx={{ mb: 2 }}>
                Topic details have been saved successfully.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(-1);
                  }}
                >
                  OK
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddTopic;